<template>
  <div class="container px-4 pt-1">
    <Header
      :screenTitle="t('PAYJOY_CARD_VIEWS.SHIPPING_FORM_TITLE_2')"
      :navbarType="'goBack'"
      :showProgressBar="true"
      :progresPercentage="progressPercentage"
      @clicked="handleBackClick"
    />
    <ShippingAddressHeader />

    <div>
      <div class="d-flex flex-column mt-1 mb-2 justify-content-center">
        <TextInput
          :id="'autocomplete_co'"
          :type="'text'"
          :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_STREET')"
          :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_STREET')"
          v-model:value="streetField"
          :errorMessage="streetNumberFieldError"
          :errorIcon="false"
          :highlight="highlightFields"
          class="autocomplete"
        />
      </div>
      <div class="d-flex flex-column mt-1 mb-2 justify-content-center">
        <TextInput
          :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_APARTMENT_LABEL')"
          :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_APARTMENT')"
          :fill="true"
          :max-length="15"
          v-model:value="apartmentField"
          :error="apartmentFieldError !== ''"
          :errorMessage="apartmentFieldError"
        />
      </div>
      <div v-if="!zoneWithCoverage.hasCoverage && !!zoneWithCoverage.error">
        <p class="error-msg">{{ zoneWithCoverage.error }}</p>
      </div>
      <div class="d-flex flex-column mt-3">
        <Autocomplete
          :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_CITY')"
          :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_CITY')"
          v-model="cityField"
          :options="filteredCities"
          :error="cityFieldError !== ''"
          :errorMessage="cityFieldError"
          @update:modelValue="handleCitySelection"
          :fill="true"
        />
      </div>

      <div class="d-flex flex-column mt-3">
        <Dropdown
          :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_REGION')"
          :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_REGION')"
          v-model="regionField"
          :options="availableRegions"
          :error="regionFieldError !== ''"
          :errorMessage="regionFieldError"
          :disabled="!cityField"
          :fill="true"
        />
      </div>

      <div class="d-flex flex-column mt-3">
        <TextInput
          :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_NEIGHBORHOOD')"
          :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_NEIGHBORHOOD')"
          :fill="true"
          v-model:value="neighborhoodField"
          :error="neighborhoodFieldError !== ''"
          :errorMessage="neighborhoodFieldError"
        />
      </div>

      <div class="d-flex flex-column mt-3">
        <TextInput
          :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_POSTAL_CODE')"
          :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_POSTAL_CODE')"
          :maxLength="6"
          :type="'number'"
          v-model:value="postalCodeField"
          :error="!isZipCodeCorrectLength"
          :errorMessage="zipErrorMessage"
          :highlight="highlightFields"
          @input="handlePostalCodeInput"
        />
      </div>

      <div class="d-flex flex-column mt-3">
        <TextInput
          :label="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_NOTES')"
          :placeholder="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_NOTES_EXPLAINER')"
          :fill="true"
          :max-length="30"
          v-model:value="notesField"
          @keyup.enter="handleNotesEnter"
        />
      </div>
    </div>

    <div class="floating-btn">
      <Button
        id="btn-continue"
        class="text-start w-100 p-3"
        @click="onConfirmClick"
      >
        <div class="text-white text-center my-auto fw-bold btn-text">
          <span>{{ t("PAYJOY_CARD_VIEWS.SUBMIT") }}</span>
        </div>
      </Button>
      <Button
        v-if="!hideCancelButton"
        id="btn-continue"
        class="text-start w-100 p-3"
        @click="onCancelClick"
        :secondary="true"
      >
        <div class="text-center my-auto fw-bold btn-text">
          <span>{{ t("PAYJOY_CARD_VIEWS.BACK_TO_USER_DATA") }}</span>
        </div>
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, inject } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Button from "@/apps/credit-line/components/Button.vue";
import TextInput from "@/apps/credit-line/components/TextInput.vue";
import Autocomplete from "@/apps/credit-line/components/Autocomplete.vue";
import Dropdown from "@/apps/credit-line/components/Dropdown.vue";
import ShippingAddressHeader from "../components/ShippingAddressHeader.vue";
import { useShippingAddressForm } from "../composables/useShippingAddressForm";
import { useCityRegionData } from "../composables/useCityRegionData";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { useGoogleApiAutocompleteIntegration } from "@/apps/credit-line/components/CreditCard/ShippingAddress/composables/GoogleApiAutocompleteIntegration";
import { useStore } from "vuex";
import Header from "@/shared-components/consumer/Header.vue";

const { t } = useI18n();

interface Props {
  hideCancelButton?: boolean;
  source?: string;
}

const props = withDefaults(defineProps<Props>(), {
  hideCancelButton: false,
  source: "",
});

const autocompleteSelectedStreetName = ref<string | null>(null);
const isStreetNameAutoCompleteUsed = ref<boolean>(false);
const isFinalStreetNameManuallyFilled = ref<boolean>(false);
const isStreetNumberAutoCompleteUsed = ref<boolean>(false);
const progressPercentage = ref<number>(99);
const store = useStore();
const creditLineId = inject("creditLineId");
const deviceTag = inject("deviceTag");
const isGoogleAutocompleteEnabled = inject("isGoogleAutocompleteEnabled");
const logsData = ref<
  Record<string, string | number | boolean | null | undefined>
>({});

const emit = defineEmits<{
  (e: "onContinueClick", data: unknown): void;
  (e: "onCancelClick"): void;
  (e: "goBackClick", data: unknown): void;
}>();

const {
  streetField,
  streetFieldError,
  apartmentField,
  apartmentFieldError,
  neighborhoodField,
  neighborhoodFieldError,
  cityField,
  cityFieldError,
  regionField,
  regionFieldError,
  postalCodeField,
  isZipCodeUserProvided,
  streetNumberFieldError,
  validateRequiredFields,
  getFormData,
  shippingAttemptId,
  isGoogleAutocompleteShown,
  zoneWithCoverage,
  notesField,
} = useShippingAddressForm("regionAndCity", deviceTag?.value || "");

const { cityRegionData } = useCityRegionData();
const {
  loadGoogleMapsScript,
  setupAutocomplete,
  components,
  isProposedAddressSelected,
  isZipCodeAutoCompleteFilled,
  highlightFields,
} = useGoogleApiAutocompleteIntegration();

const isZipCodeCorrectLength = ref<boolean>(true);
const isCityValid = ref<boolean>(true);
const zipErrorMessage = ref<string>("");
let startedFillout = false;

const filteredCities = computed(() => {
  if (!cityField.value) {
    // Get unique cities using Set
    return [...new Set(cityRegionData.value.map((item) => item.city))];
  }
  const searchTerm = cityField.value.toLowerCase();
  // Filter and remove duplicates
  return [
    ...new Set(
      cityRegionData.value
        .filter((item) => item.city.toLowerCase().includes(searchTerm))
        .map((item) => item.city)
    ),
  ];
});

const handlePostalCodeInput = () => {
  if (postalCodeField.value !== "") {
    isZipCodeUserProvided.value = true;
  }
  zipLengthValidation(postalCodeField.value);
};

const handleBackClick = () => {
  emit("goBackClick", {});
};

const zipLengthValidation = (zipCode) => {
  if (zipCode.length === 6 || zipCode === "") {
    isZipCodeCorrectLength.value = true;
  } else {
    isZipCodeCorrectLength.value = false;
    zipErrorMessage.value = t("PAYJOY_CARD_VIEWS.ZIP_LENGTH_ERROR");
  }
};

const availableRegions = computed(() => {
  if (!cityField.value) return [];
  const cityData = cityRegionData.value.filter(
    (item) => item.city === cityField.value
  );
  return cityData ? [...new Set(cityData.map((item) => item.region))] : [];
});

const handleCitySelection = (city: string) => {
  cityField.value = city;
  const cityData = cityRegionData.value.find((item) => item.city === city);
  if (cityData) {
    regionField.value = cityData.region;
  }
};

const validateCity = (city: string): boolean => {
  return filteredCities.value.includes(city);
};

watch(
  () => postalCodeField,
  (newValue) => {
    zipLengthValidation(newValue);
  }
);

watch(streetField, (newVal) => {
  if (newVal !== "" && !startedFillout) {
    startedFillout = true;
    logAnalyticsEvent(
      AnalyticEvents.CreditLine.CcShippingStartFillout,
      "CC_Details"
    );
    return;
  }
  if (
    autocompleteSelectedStreetName.value &&
    newVal !== autocompleteSelectedStreetName.value
  ) {
    isFinalStreetNameManuallyFilled.value = true;
    return;
  }
});

watch(
  () => cityField.value,
  (newVal) => {
    const cityData = cityRegionData.value.find(
      (item) =>
        item.city === newVal ||
        (newVal === "CARTAGENA DE INDIAS" && item.city === "CARTAGENA")
    );

    regionField.value = cityData?.region || "";

    if (newVal === "CARTAGENA DE INDIAS") {
      cityField.value = "CARTAGENA";
      isCityValid.value = true;
    } else if (!validateCity(newVal)) {
      isCityValid.value = false;
      cityFieldError.value = t("PAYJOY_CARD_VIEWS.INVALID_CITY");
    } else {
      isCityValid.value = true;
      cityFieldError.value = "";
    }
  }
);

watch(
  () => components,
  (newComponents) => {
    postalCodeField.value = newComponents.postalCode || "";
    streetField.value = (
      (newComponents.street || "") +
      " " +
      (newComponents.streetNumber || "")
    ).trim();
    regionField.value = newComponents.region || "";
    neighborhoodField.value = newComponents.neighborhood || "";
    cityField.value = (newComponents.city || "")
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  },
  { deep: true }
);

const handleNotesEnter = () => {
  if (canSubmitForm()) {
    onConfirmClick();
  }
};

const canSubmitForm = () => {
  return (
    zoneWithCoverage.hasCoverage &&
    !streetFieldError.value &&
    !cityFieldError.value &&
    isCityValid.value &&
    isZipCodeCorrectLength.value &&
    streetField.value !== "" &&
    streetField.value.trim() !== "" &&
    regionField.value !== ""
  );
};

const onCancelClick = () => {
  emit("onCancelClick");
};

const onConfirmClick = async () => {
  await validateRequiredFields(props.source);
  zipLengthValidation(postalCodeField.value);
  if (canSubmitForm()) {
    emit("onContinueClick", {
      ...getFormData(),
      street_number: "", // Empty for Colombia
      additional_info: undefined, // Undefined for Colombia
      postalCode:
        postalCodeField.value === "" ? "000000" : postalCodeField.value,
    });

    logsData.value = {
      creditLineId: creditLineId?.value,
      shippingAttemptId: shippingAttemptId.value,
      isZipCodeUserProvided: isZipCodeUserProvided.value,
      isStreetNameAutoCompleteUsed: isStreetNameAutoCompleteUsed.value,
      isZipCodeAutoCompleteFilled: isZipCodeAutoCompleteFilled.value,
      isFinalStreetNameManuallyFilled: isFinalStreetNameManuallyFilled.value,
      isProposedAddressSelected: isProposedAddressSelected.value,
      isStreetNumberAutoCompleteUsed: isStreetNumberAutoCompleteUsed.value,
      isGoogleAutocompleteShown: isGoogleAutocompleteShown.value,
    };

    store.dispatch(
      "creditLine/addShipmentLog",
      "Logs of how user used the forms: " + JSON.stringify(logsData.value)
    );

    logAnalyticsEvent(
      AnalyticEvents.CreditLine.CcShippingFormSentLogs,
      "CC_Details",
      "CcShippingFormSentLogs",
      logsData.value
    );
  }
};

onMounted(async () => {
  shippingAttemptId.value = "OLD" + creditLineId?.value + Date.now();
  if (isGoogleAutocompleteEnabled.value) {
    isGoogleAutocompleteShown.value = false;

    logAnalyticsEvent(
      AnalyticEvents.CreditLine.GoogleAutocompleteNotShown,
      "CC_Details"
    );
  } else {
    isGoogleAutocompleteShown.value = true;

    logAnalyticsEvent(
      AnalyticEvents.CreditLine.GoogleAutocompleteShown,
      "CC_Details"
    );

    try {
      await loadGoogleMapsScript();

      const inputStreet = document.getElementById("autocomplete_co");

      setupAutocomplete(inputStreet, "CO");
    } catch (error) {
      console.error("Error loading Google Maps script:", error);
    }
  }

  logAnalyticsEvent(
    AnalyticEvents.CreditLine.CoShippingFormLoaded,
    "CC_Details"
  );
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.field {
  width: 60%;
  margin-right: 5px;
}

.field-attachment {
  width: 60%;
}

.floating-btn {
  background-color: white;
  left: 0;
  bottom: 0;
  z-index: 200;
  height: 120px;
  width: 100%;
}

.input-wrapper {
  height: 50px;
  background-color: white;
  border: 1px solid $pj-color-light-gray;
  border-radius: 11px;
  padding: 5px 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;

  &:focus-within {
    border: 1px solid $pj-color-black;
  }

  &.fill {
    width: 100%;
  }

  &.error {
    border: 1px solid $pj-color-red;
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}

.error-msg {
  color: $pj-color-red;
}

.input-label {
  position: absolute;
  top: 2px;
  left: 20px;
  font-size: 12px;
  color: $pj-color-cool-gray;
}

.input-element {
  color: $pj-color-black;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  border: none;
  flex-grow: 1;
  height: 100%;
  width: 100%;

  &:focus-visible,
  &:focus,
  &:active {
    outline-width: 0;
  }

  &::placeholder {
    color: $pj-color-cool-gray;
  }

  &.with-label {
    padding-top: 15px;
  }

  &.shrink {
    flex-shrink: 20;
  }
}

.error-msg {
  color: $pj-color-red;
}
</style>
